// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import { useSearchParams } from "react-router-dom";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import Heading from "../UI/Heading";
import { MdCheck, MdClose, MdEdit, MdRefresh } from "react-icons/md";
import PersonCard from "../UI/PersonCard";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import { isoDateToString } from "../../util/format-date";
import { UtilityContext } from "../../context/util-context";

// Internal Imports

/** ========================================================================
 * * OppotunityInfo Component
 * This component renders a page to show an opportunity's information.
 * ========================================================================== */

export default function OpportunityInfo() {
  const { navigate } = useContext(UtilityContext);
  // Get Employee ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [opp, setOpp] = useState({});
  const [employees, setEmployees] = useState([]);
  const [edit, setEdit] = useState(false);

  /* ====================== MAP FIELDS  ====================== */
  const mapFields = (opp) => {
    const customFields = opp?.contact?.customField;
    if (!customFields || customFields.length < 1) return opp;

    // Map Each Field
    customFields.forEach((field) => {
      switch (field.id) {
        case "0RZtfc09kxw1bgDkLcRJ":
          opp.brands = field.value;
          break;
        case "HrspKfpTgkDZNdjL9Ehh":
          opp.milesNeeded = field.value;
          break;
        case "Ttje4w7lBPgkjS68F3PA":
          opp.startIntent = field.value;
          break;
      }
    });
    return opp;
  };

  /* ====================== FETCH OPPORTUNITY  ====================== */
  const fetchOpportunity = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch-opportunity", {
        id: id,
      });
      setOpp(mapFields(response));
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };
  /* ====================== FETCH OPPORTUNITY  ====================== */
  const fetchEmployees = async () => {
    try {
      const emps = await requestAdminAPI("filter", {
        table: "employee",
        filters: [`active: { eq: true }`, `ghlId: { ne: null }`],
      });
      setEmployees(emps);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== EDIT OPPORTUNITY ====================== */
  const submitHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        id: id,
        assignTo: document.getElementById("assignTo")?.value,
        stageId: document.getElementById("stage")?.value,
        value: document.getElementById("budget")?.value,
        fields: {},
      };
      ["brands", "milesNeeded", "startIntent"].forEach((field) => {
        const value = document.getElementById(field)?.value;
        if (value !== opp[field]) payload.fields[field] = value;
      });

      // Send Request
      await requestAdminAPI("edit-opportunity", payload);
      setEdit(false);
      fetchOpportunity();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== ADD NOTE ====================== */
  const addNoteHandler = async () => {
    try {
      setLoading(true);
      // Send Request
      const res = await requestAdminAPI("edit-opportunity", {
        id: id,
        note: document.getElementById("note").value,
      });
      if (res) document.getElementById("note").value = "";
      fetchOpportunity();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== SEND QUOTE ====================== */
  const sendQuoteHandler = async () => {
    navigate(
      `/quotes/add?opportunityId=${id}&name=${opp?.name}&phone=${opp?.contact?.phone}&email=${opp?.contact?.email}`
    );
  };

  useEffect(() => {
    fetchEmployees();
    fetchOpportunity();
  }, []);

  if (!id || (!loading && (!opp || !opp?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Opportunity ID Not Provided or Opportunity Not Found!
        </p>
      </div>
    );

  return (
    <div className="container gap-8">
      <Heading title="Opportunity Information" id={id}>
        {edit ? (
          <Fragment>
            <MdClose className="icon" onClick={() => setEdit(false)} />
            <MdCheck className="icon" onClick={() => submitHandler()} />
          </Fragment>
        ) : (
          <MdEdit className="icon" onClick={() => setEdit(true)} />
        )}
        <MdRefresh className="icon" onClick={fetchOpportunity} />
      </Heading>

      <div className="info lg:grid-cols-2">
        <PersonCard
          label={opp?.contact?.phone}
          name={opp?.name}
          secondary={opp?.contact?.email}
          link={`/customers?query=${opp?.contact?.phone}`}
          loading={loading}
        />
        {edit ? (
          <SelectPair
            label="Assign To"
            edit={true}
            options={employees.map((emp) => ({
              id: emp.ghlId,
              name: emp.name,
            }))}
            inputOptions={{
              defaultValue: opp?.assignedTo,
              id: "assignTo",
            }}
          />
        ) : (
          <PersonCard
            label="Assigned To"
            name={opp?.employee?.name}
            image={opp?.employee?.photo}
            secondary={opp?.employee?.phone}
            loading={loading}
          />
        )}
      </div>
      <div className="info lg:grid-cols-5">
        <DetailPair
          label="Date Added"
          value={isoDateToString(opp?.createdAt)}
          loading={loading}
        />
        <DetailPair label="Source" value={opp?.source} loading={loading} />
        <SelectPair
          label="Stage"
          value={opp?.stage?.name}
          edit={edit}
          options={opp?.pipeline?.stages.map((stage) => ({
            id: stage.id,
            name: stage.name,
          }))}
          inputOptions={{
            defaultValue: opp?.stage?.id,
            id: "stage",
          }}
          loading={loading}
        />
        <DetailPair
          label="Budget"
          value={opp?.monetaryValue && `$${opp?.monetaryValue}`}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "budget",
            type: "number",
            defaultValue: opp?.monetaryValue,
          }}
        />
        <DetailPair
          label="Miles Needed"
          value={opp?.milesNeeded}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "milesNeeded",
            type: "text",
            defaultValue: opp?.milesNeeded || "",
          }}
        />
        <DetailPair
          label="Preferred Brands"
          value={opp?.brands}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "brands",
            type: "text",
            defaultValue: opp?.brands || "",
          }}
        />
        <DetailPair
          label="Start Intent"
          value={opp?.startIntent}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "startIntent",
            type: "text",
            defaultValue: opp?.startIntent || "",
          }}
        />
        {/* <SelectPair
          label="Appointment Location"
          edit={edit}
          value={opp?.apptLocationId}
          options={[
            ...locations.map((loc) => ({
              id: loc.id,
              name: loc.name,
            })),
            { id: "delete", name: "Cancel Appointment" },
          ]}
          inputOptions={{
            defaultValue: opp?.apptLocationId,
            id: "location",
          }}
          loading={loading}
        />
        <DetailPair
          label="Appointment Date"
          edit={edit}
          value={isoDateToString(opp?.apptDate)}
          inputOptions={{
            id: "date",
            type: "date",
            defaultValue: isoDateToString(opp?.apptDate, "yyyy-MM-dd"),
          }}
          loading={loading}
        />
        <DetailPair
          label="Appointment Vehicle"
          edit={edit}
          value={isoDateToString(opp?.apptDate)}
          inputOptions={{
            id: "apptVehicleId",
            type: "date",
            defaultValue: isoDateToString(opp?.apptDate, "yyyy-MM-dd"),
          }}
          loading={loading}
        /> */}
      </div>
      <div className="grid gap-8 lg:grid-cols-2">
        <OpportunityNotes
          notes={opp?.notes}
          employees={employees}
          loading={loading}
          addHandler={addNoteHandler}
        />
        <OpportunityQuotes
          quotes={opp?.quotes}
          loading={loading}
          addHandler={sendQuoteHandler}
        />
      </div>
    </div>
  );
}

/** ========================================================================
 * * OpportunityNotes Component
 * ========================================================================== */
const OpportunityNotes = ({ notes, employees, loading, addHandler }) => {
  const empMap = {};
  employees.forEach((emp) => {
    empMap[emp.ghlId] = emp.name;
  });
  return (
    <div className="grid content-start gap-8">
      <Heading title="Notes" isBackArrow={false} />
      {loading ? (
        <LoadingCard columns={4} max={1} />
      ) : notes?.length > 0 ? (
        <div className="text-sm list text-stone-200">
          {notes?.map((note, idx) => (
            <div key={idx} className="grid gap-4 lg:grid-cols-5">
              <p className="text-stone-400">
                {isoDateToString(note.createdAt)}
              </p>
              <p className="lg:col-span-3">{note.body}</p>
              <p>{empMap[note.createdBy] || "Unknown"}</p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      {!loading && (
        <div className="info lg:grid-cols-4">
          <input
            id="note"
            type="text"
            autoComplete="off"
            className="form-input lg:col-span-3"
            placeholder="Type note here..."
          />
          <button className="btn-primary" onClick={addHandler}>
            Add Note
          </button>
        </div>
      )}
    </div>
  );
};

/** ========================================================================
 * * OpportunityQuotes Component
 * ========================================================================== */
const OpportunityQuotes = ({ quotes, loading, addHandler }) => {
  return (
    <div className="grid content-start gap-8">
      <Heading title="Quotes" isBackArrow={false} />
      {loading ? (
        <LoadingCard columns={4} max={1} />
      ) : quotes?.length > 0 ? (
        <div className="text-sm list text-stone-200">
          {quotes?.map((quote, idx) => (
            <div
              key={idx}
              onClick={() => window.open(quote.link, "_blank")}
              className="grid lg:grid-cols-5 pill"
            >
              <p>{isoDateToString(quote.dateCreated)}</p>
              <p>{quote.name}</p>
              <p>{quote.email}</p>
              <p>{quote.phone}</p>
              <p>{quote.createdBy?.name}</p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}
      {!loading && (
        <button
          className="w-40 ml-auto text-sm btn-primary"
          onClick={addHandler}
        >
          Send Quote
        </button>
      )}
    </div>
  );
};
